import styled from '@emotion/styled';
import Button from '@shared/components/core/button/CoreButton';
import { BodyXSmall } from '@shared/components/core/text/Typography';
import TinyButton from '@shared/components/core/tiny/TinyButton';
import TinyHeader from '@shared/components/core/tiny/TinyHeader';
import TinyLabel from '@shared/components/core/tiny/TinyLabel';
import { statusIcon, statusTitle, statusVariant } from '@shared/constants/extensions/mcpStatus';
import { McpConnectionStatus } from '@shared/models/mcp/McpServerModel';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/** Which half of the connect flow is running, so the button can say what it's waiting on. */
export type McpConnectBusy = 'test' | 'oauth' | null;

export interface IMcpConnectionRowProps {
    status: McpConnectionStatus;
    /** The last failure's message, shown under the row. */
    error?: string;
    busy: McpConnectBusy;
    /** Disables everything while the surface is busy with something else (a save, an API call). */
    disabled?: boolean;
    /** True when OAuth sign-in still has to happen before a test can succeed. */
    needsOauth?: boolean;
    /** True when the server is signed in, so re-authorizing and signing out are offered. */
    canDisconnect?: boolean;
    onTest: () => void;
    /** Re-run sign-in for tokens that have gone stale. Required when `canDisconnect`. */
    onReconnect?: () => void;
    /** Return the in-flight promise so the row can tell a completed sign-out from a failed one. */
    onDisconnect?: () => void | Promise<unknown>;
    /** Anything to show beside the status — e.g. how many tools the last test found. */
    children?: React.ReactNode;
}

/**
 * The one action that makes a server work: save-and-test, signing in first when OAuth hasn't happened
 * yet. Shared by the editor and the detail dialog so "Connect" means the same thing, looks the same, and
 * reports the same status in both — the editor used to have its own copy that drifted from the dialog's.
 *
 * Purely presentational: the caller owns the flow (persisting first, running OAuth, storing the result)
 * and passes back what it produced.
 */
export default function McpConnectionRow({
    status,
    error,
    busy,
    disabled,
    needsOauth,
    canDisconnect,
    onTest,
    onReconnect,
    onDisconnect,
    children,
}: Readonly<IMcpConnectionRowProps>) {
    const { t } = useTranslation('common');
    const locked = busy !== null || !!disabled;

    /**
     * A completed sign-out that the caller's own props may not reflect yet. Callers whose data is live
     * (rather than a snapshot) only catch up an async round-trip after the disconnect resolves, which
     * would re-enable this row's buttons for a beat and then flip them — so hold the signed-out result
     * here until those props agree. Cleared by the effect below, or when a new connect supersedes it.
     */
    const [signedOut, setSignedOut] = useState(false);
    useEffect(() => {
        if (!canDisconnect) setSignedOut(false);
    }, [canDisconnect]);

    // Signing out invalidates whatever the last connection reported, so a held sign-out speaks for the
    // whole row: no status, no stale error, and back to offering sign-in.
    const resolved = signedOut
        ? { status: 'unknown' as McpConnectionStatus, error: undefined, needsOauth: true, canDisconnect: false }
        : { status, error, needsOauth, canDisconnect };

    // While a connect/test/disconnect flow runs, the values above can change several times in quick
    // succession (each sign-in/test step reloads the caller's data), which would restructure the row
    // through states too brief to read — the flicker. Hold the pill and the OAuth affordances to their
    // pre-flow snapshot until the flow ends, so the row settles exactly once. The button still animates
    // Connecting…/Testing… because its label reads the live `busy`.
    const steady = useRef(resolved);
    if (busy === null) {
        steady.current = resolved;
    }
    const view = steady.current;

    /** Clear a held sign-out when the user starts connecting again, so the fresh outcome shows through. */
    const startConnect = (run: () => void) => () => {
        setSignedOut(false);
        run();
    };

    const runDisconnect = async () => {
        if (!onDisconnect) return;
        try {
            await onDisconnect();
            setSignedOut(true);
        } catch {
            // The caller surfaces the failure through `error`; there's no sign-out to hold onto.
        }
    };

    return (
        <>
            <TinyHeader size="small" title={t('mcp.status.label')} />
            <ConnectionRow>
                <ConnectButton
                    $color={view.needsOauth ? 'primary' : 'simple'}
                    $size="small"
                    startIcon={view.needsOauth ? 'connect' : 'sync'}
                    disabled={locked}
                    onClick={startConnect(onTest)}>
                    {busy === 'oauth'
                        ? t('mcp.oauth.connecting')
                        : busy === 'test'
                          ? t('mcp.status.testing')
                          : view.needsOauth
                            ? t('mcp.oauth.connect')
                            : t('mcp.editor.testConnection')}
                </ConnectButton>
                {/* Reconnect re-runs sign-in for tokens that have gone stale; Disconnect drops them
                    entirely, which is the user's call to make wherever the server is shown. */}
                {view.canDisconnect && (
                    <>
                        {onReconnect && (
                            <TinyButton
                                title={t('mcp.oauth.reconnect')}
                                variant="underline_diminished"
                                disabled={locked}
                                onClick={startConnect(onReconnect)}
                            />
                        )}
                        {onDisconnect && (
                            <TinyButton
                                title={t('mcp.oauth.disconnect')}
                                variant="underline_diminished"
                                disabled={locked}
                                onClick={runDisconnect}
                            />
                        )}
                    </>
                )}
                <TinyLabel
                    title={t('mcp.status.label')}
                    // "Connection Connected" says the same thing twice — the status word and its icon
                    // carry it on their own, so the label is left for screen readers.
                    hideLabel={true}
                    value={statusTitle(t, view.status)}
                    icon={statusIcon(view.status)}
                    variant={statusVariant(view.status)}
                />
                {children}
            </ConnectionRow>
            {view.error && <ErrorText>{view.error}</ErrorText>}
        </>
    );
}

/** Outlined even in its 'simple' colour, so the row's one real action still reads as a button. */
const ConnectButton = styled(Button)<{ $color: 'primary' | 'simple' }>`
    border: 1px solid ${(props) => props.theme.color.textBody};
`;

const ConnectionRow = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem;
`;

const ErrorText = styled(BodyXSmall)`
    color: ${(props) => props.theme.color.error};
`;
